import type { CreativeWatchlistApi } from '../api/CreativeWatchlistApi'
import type { AtcDraft, AtcPayloadConfig } from '../models/watchlist.types'
import { validateDrafts } from '../service/atcConfig'

export type SaveAtcWatchlistOutcome =
  | { status: 'invalid'; errors: ReturnType<typeof validateDrafts>['errors']; capExceeded: boolean }
  | { status: 'canceled' | 'unchanged' }
  | { status: 'saved'; configs: AtcPayloadConfig[] }
  | { status: 'deleted'; configs: [] }

export interface SaveAtcWatchlistOptions {
  accountId: number | string
  workspaceId: number | string
  drafts: readonly AtcDraft[]
  hasExistingConfig: boolean
  confirmDelete: () => Promise<boolean>
}

type AtcWriteApi = Pick<
  CreativeWatchlistApi,
  'saveAtcConfigs' | 'deleteAtcConfigs' | 'getAtcConfigSnapshot'
>

export class SaveAtcWatchlistFlow {
  constructor(private readonly api: AtcWriteApi) {}

  async execute(options: SaveAtcWatchlistOptions): Promise<SaveAtcWatchlistOutcome> {
    const validation = validateDrafts(options.drafts)
    if (!validation.valid) {
      return {
        status: 'invalid',
        errors: validation.errors,
        capExceeded: validation.capExceeded,
      }
    }

    if (validation.payload.length === 0) {
      if (!options.hasExistingConfig) return { status: 'unchanged' }
      if (!(await options.confirmDelete())) return { status: 'canceled' }
      await this.api.deleteAtcConfigs(options.accountId, options.workspaceId)
      return { status: 'deleted', configs: [] }
    }

    await this.api.saveAtcConfigs(
      options.accountId,
      options.workspaceId,
      validation.payload,
      options.hasExistingConfig,
    )
    const refreshed = await this.api.getAtcConfigSnapshot(options.accountId, options.workspaceId)
    return { status: 'saved', configs: refreshed.configs }
  }
}
